import React, { Component } from "react";
import { connect } from "react-redux";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

import { call } from "../../util/action";
import { translate } from "../../util/util";
import { OVERVIEW } from "../../config/lang";
import "./style.scss";

// Design-token colours (kept in sync with :root --md-* in modern-theme.css)
const C = {
  primary: "#00ABEB",
  tertiary: "#6672B0",
  secondary: "#5B7A8A",
  grid: "rgba(0, 40, 80, 0.08)",
  axis: "#6B7680",
};

const PAGE_SIZE = 10;

const KPI_META = [
  { key: "active_merchandisers", icon: "badge", color: C.primary },
  { key: "active_supervisors", icon: "supervisor_account", color: C.tertiary },
  { key: "visits", icon: "location_on", color: C.secondary },
  { key: "reports", icon: "bar_chart", color: C.primary },
  { key: "answers", icon: "quiz", color: C.tertiary },
];

const TREND_SERIES = [
  { key: "visits", color: C.primary },
  { key: "reports", color: C.tertiary },
  { key: "answers", color: C.secondary },
];

const LOCALE = { uk: "uk-UA", en: "en-US", hy: "hy-AM", ka: "ka-GE" };

function pad2(n) {
  return String(n).padStart(2, "0");
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

function addMonths(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

function computeDelta(current, previous) {
  if (!previous) {
    if (!current) return { pct: 0, dir: "flat" };
    return { pct: 100, dir: "up" };
  }
  const raw = Math.round(((current - previous) / previous) * 100);
  return { pct: Math.abs(raw), dir: raw > 0 ? "up" : raw < 0 ? "down" : "flat" };
}

// Merge per-metric daily arrays into a single [{ day, visits, reports, answers }]
function mergeTimeseries(ts) {
  const byDay = {};
  ["visits", "reports", "answers"].forEach(metric => {
    (ts[metric] || []).forEach(({ date, count }) => {
      if (!byDay[date]) byDay[date] = { date };
      byDay[date][metric] = count;
    });
  });
  return Object.keys(byDay)
    .sort()
    .map(date => ({
      day: Number(date.slice(8, 10)),
      visits: 0,
      reports: 0,
      answers: 0,
      ...byDay[date],
    }));
}

class OverviewView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stats: null,
      loading: true,
      busy: false,
      error: false,
      month: currentMonth(),
      search: "",
      page: 1,
    };
    this.searchTimer = null;
  }

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  load = () => {
    const { month, search, page } = this.state;
    this.setState({ busy: true, error: false });
    const params = new URLSearchParams();
    if (month) params.set("month", month);
    if (search) params.set("search", search);
    params.set("page", page);
    params.set("page_size", PAGE_SIZE);
    call({ method: "GET", url: `stats/dashboard/?${params.toString()}` }, res => {
      if (res && res.kpis) {
        this.setState({ stats: res, loading: false, busy: false });
      } else {
        this.setState({ loading: false, busy: false, error: true });
      }
    });
  };

  changeMonth = delta => {
    if (this.state.busy) return;
    const next = addMonths(this.state.month, delta);
    if (delta > 0 && next > currentMonth()) return; // no future months
    this.setState({ month: next, page: 1 }, this.load);
  };

  onSearch = e => {
    const value = e.target.value;
    this.setState({ search: value, page: 1 });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(this.load, 400);
  };

  changePage = delta => {
    const { by_merchandiser } = this.state.stats;
    const next = this.state.page + delta;
    if (this.state.busy || next < 1 || next > by_merchandiser.num_pages) return;
    this.setState({ page: next }, this.load);
  };

  lang() {
    return (this.props.me && this.props.me.language) || "en";
  }

  t = key => translate(OVERVIEW, key, this.lang());

  periodLabel() {
    const { stats } = this.state;
    if (!stats) return "";
    const d = new Date(stats.period.current.from);
    try {
      return d.toLocaleDateString(LOCALE[this.lang()] || "en-US", {
        month: "long",
        year: "numeric",
      });
    } catch (e) {
      return stats.period.current.from;
    }
  }

  renderMonthNav() {
    const atCurrent = this.state.month >= currentMonth();
    return (
      <div className="ov-month-nav">
        <button
          type="button"
          className="ov-month-btn"
          onClick={() => this.changeMonth(-1)}
          disabled={this.state.busy}
          aria-label="previous month"
        >
          <span className="material-symbols-outlined">chevron_left</span>
        </button>
        <span className="ov-month-label">{this.periodLabel()}</span>
        <button
          type="button"
          className="ov-month-btn"
          onClick={() => this.changeMonth(1)}
          disabled={this.state.busy || atCurrent}
          aria-label="next month"
        >
          <span className="material-symbols-outlined">chevron_right</span>
        </button>
      </div>
    );
  }

  renderKpis() {
    const { kpis } = this.state.stats;
    return (
      <div className="ov-kpi-grid">
        {KPI_META.map(meta => {
          const item = kpis[meta.key] || { current: 0, previous: 0 };
          const d = computeDelta(item.current, item.previous);
          const dirIcon =
            d.dir === "up" ? "trending_up" : d.dir === "down" ? "trending_down" : "trending_flat";
          const chipClass =
            d.dir === "up"
              ? "pt-status-active"
              : d.dir === "down"
              ? "pt-status-removed"
              : "ov-chip-flat";
          return (
            <div className="ov-kpi-card" key={meta.key}>
              <div className="ov-kpi-icon" style={{ color: meta.color, background: `${meta.color}1a` }}>
                <span className="material-symbols-outlined">{meta.icon}</span>
              </div>
              <div className="ov-kpi-body">
                <div className="ov-kpi-value">{item.current.toLocaleString()}</div>
                <div className="ov-kpi-label">{this.t(meta.key)}</div>
              </div>
              <div className={"ov-kpi-chip pt-status-pill " + chipClass}>
                <span className="material-symbols-outlined">{dirIcon}</span>
                {d.pct}%
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderTrend() {
    const data = mergeTimeseries(this.state.stats.timeseries);
    return (
      <div className="ov-card">
        <div className="ov-card-head">
          <h5>{this.t("trend_title")}</h5>
          <div className="ov-legend">
            {TREND_SERIES.map(s => (
              <span key={s.key} className="ov-legend-item">
                <i style={{ background: s.color }} />
                {this.t(s.key)}
              </span>
            ))}
          </div>
        </div>
        {data.length === 0 ? (
          <div className="ov-empty">{this.t("empty")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
              <defs>
                {TREND_SERIES.map(s => (
                  <linearGradient id={`ov-grad-${s.key}`} key={s.key} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} stroke={C.grid} />
              <XAxis dataKey="day" tick={{ fontSize: 12, fill: C.axis }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 12, fill: C.axis }} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "none", boxShadow: "var(--md-elevation-2)" }} />
              {TREND_SERIES.map(s => (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={this.t(s.key)}
                  stroke={s.color}
                  strokeWidth={2}
                  fill={`url(#ov-grad-${s.key})`}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  }

  renderTopMerchandisers() {
    const top = this.state.stats.top_merchandisers || [];
    return (
      <div className="ov-card">
        <div className="ov-card-head">
          <h5>{this.t("top_merchandisers")}</h5>
        </div>
        {top.length === 0 ? (
          <div className="ov-empty">{this.t("empty")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={top} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid horizontal={false} stroke={C.grid} />
              <XAxis type="number" tick={{ fontSize: 12, fill: C.axis }} tickLine={false} axisLine={false} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                width={140}
                tick={{ fontSize: 12, fill: C.axis }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: "rgba(0,40,80,0.04)" }}
                contentStyle={{ borderRadius: 12, border: "none", boxShadow: "var(--md-elevation-2)" }}
              />
              <Bar dataKey="visits" name={this.t("visits")} radius={[0, 6, 6, 0]}>
                {top.map((entry, i) => (
                  <Cell key={i} fill={C.primary} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  }

  renderTable() {
    const table = this.state.stats.by_merchandiser;
    const rows = table.results;
    return (
      <div className="ov-card">
        <div className="ov-card-head">
          <h5>{this.t("by_merchandiser")}</h5>
          <div className="ov-search">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              value={this.state.search}
              onChange={this.onSearch}
              placeholder={this.t("search_ph")}
            />
          </div>
        </div>
        <div className="pt-table-wrap table-scroll-x">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>{this.t("col_merchandiser")}</th>
                <th className="ov-num">{this.t("col_visits")}</th>
                <th className="ov-num">{this.t("col_reports")}</th>
                <th className="ov-num">{this.t("col_answers")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="ov-empty">
                    {this.t("empty")}
                  </td>
                </tr>
              ) : (
                rows.map(r => (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td className="ov-num">{r.visits.toLocaleString()}</td>
                    <td className="ov-num">{r.reports.toLocaleString()}</td>
                    <td className="ov-num">{r.answers.toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {table.num_pages > 1 && (
          <div className="ov-pagination">
            <button
              type="button"
              className="ov-month-btn"
              onClick={() => this.changePage(-1)}
              disabled={this.state.busy || table.page <= 1}
              aria-label="previous page"
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <span className="ov-page-info">
              {table.page} / {table.num_pages}
              <span className="ov-page-count"> · {table.count}</span>
            </span>
            <button
              type="button"
              className="ov-month-btn"
              onClick={() => this.changePage(1)}
              disabled={this.state.busy || table.page >= table.num_pages}
              aria-label="next page"
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { loading, error, stats, busy } = this.state;
    return (
      <div className="container-fluid">
        <div className={"box p-4 ov-root" + (busy ? " ov-busy" : "")}>
          <div className="ov-header">
            <div>
              <h3 className="ov-title">{this.t("title")}</h3>
              {stats && <div className="ov-subtitle">{this.periodLabel()}</div>}
            </div>
            {stats && this.renderMonthNav()}
          </div>

          {loading && (
            <div className="ov-empty ov-loading">
              <i className="fa fa-spinner fa-spin" /> {this.t("loading")}
            </div>
          )}

          {!loading && error && (
            <div className="ov-empty">
              {this.t("error")}{" "}
              <button className="btn btn-sm btn-link" onClick={this.load}>
                {this.t("retry")}
              </button>
            </div>
          )}

          {!loading && !error && stats && (
            <React.Fragment>
              {this.renderKpis()}
              <div className="ov-charts">
                {this.renderTrend()}
                {this.renderTopMerchandisers()}
              </div>
              {this.renderTable()}
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({ me: state.me });

export default connect(mapStateToProps)(OverviewView);
